import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath } from '../utils/paths';
import PhotoGallery from './PhotoGallery';
import ScrimmageStatsSummarySection from './ScrimmageStatsSummarySection';
import './MatchSummaryForm.css';

const MatchSummaryForm = ({ type, entityId }) => {
  const { user, isAdmin, isClubAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromScrimmage = searchParams.get('fromScrimmage') === '1';
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [creatingBlogDraft, setCreatingBlogDraft] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [participants, setParticipants] = useState([]);
  const [existingSummary, setExistingSummary] = useState(null);
  const [scrimmageStats, setScrimmageStats] = useState(null);
  const [entity, setEntity] = useState(null);
  const [club, setClub] = useState(null);
  const [formData, setFormData] = useState({
    scoreHome: '',
    scoreAway: '',
    opponentName: '',
    manOfMatchPlayerId: '',
    manOfMatchReason: '',
    nextMatchId: '',
    nextMatchDate: '',
  });
  const [photos, setPhotos] = useState([]);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const applyScrimmageToForm = useCallback((scrimmage) => {
    if (!scrimmage) return;
    const top = [...(scrimmage.playerStats || [])]
      .filter((p) => p.goals + p.assists + p.saves + p.defended > 0)
      .sort((a, b) => {
        const scoreA = a.goals * 10 + a.assists * 5 + a.saves + a.defended;
        const scoreB = b.goals * 10 + b.assists * 5 + b.saves + b.defended;
        return scoreB - scoreA;
      })[0];

    setFormData((prev) => ({
      ...prev,
      scoreHome: scrimmage.scoreRed != null ? String(scrimmage.scoreRed) : prev.scoreHome,
      scoreAway: scrimmage.scoreBlue != null ? String(scrimmage.scoreBlue) : prev.scoreAway,
      manOfMatchPlayerId: top?.playerId || prev.manOfMatchPlayerId,
      manOfMatchReason: top
        ? [
            top.goals > 0 && `${top.goals} goal${top.goals > 1 ? 's' : ''}`,
            top.assists > 0 && `${top.assists} assist${top.assists > 1 ? 's' : ''}`,
            top.saves > 0 && `${top.saves} save${top.saves > 1 ? 's' : ''}`,
            top.defended > 0 && `${top.defended} defended`,
          ]
            .filter(Boolean)
            .join(', ')
        : prev.manOfMatchReason,
    }));
  }, []);

  const loadScrimmageStats = useCallback(async () => {
    if (type !== 'practice' || !user?.id) return null;
    try {
      const data = await api.getPracticeScrimmageStats(entityId, user.id);
      setScrimmageStats(data);
      return data;
    } catch {
      setScrimmageStats(null);
      return null;
    }
  }, [type, entityId, user?.id]);

  const handleSyncFromScrimmage = useCallback(async () => {
    if (!user?.id || type !== 'practice') return;
    try {
      setSyncing(true);
      setError('');
      const summary = await api.syncPracticeSummaryFromScrimmage(entityId, user.id);
      setExistingSummary(summary);
      setScrimmageStats(summary.scrimmageStats || null);
      setFormData({
        scoreHome: summary.scoreHome != null ? String(summary.scoreHome) : '',
        scoreAway: summary.scoreAway != null ? String(summary.scoreAway) : '',
        opponentName: summary.opponentName || '',
        manOfMatchPlayerId: summary.manOfMatchPlayerId || '',
        manOfMatchReason: summary.manOfMatchReason || '',
        nextMatchId: summary.nextMatchId || '',
        nextMatchDate: summary.nextMatchDate || '',
      });
      setUploadedPhotos(summary.photoUrls || []);
      setSuccess('Summary created from scrimmage stats. Add team photos and share.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to import scrimmage stats');
    } finally {
      setSyncing(false);
    }
  }, [user?.id, type, entityId]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load entity (match or practice)
      const entityData = type === 'match'
        ? await api.getMatchById(entityId)
        : await api.getPracticeById(entityId);
      setEntity(entityData);

      if (entityData?.clubId && user?.id) {
        try {
          const clubData = await api.getClubById(entityData.clubId);
          setClub(clubData);
        } catch {
          setClub(null);
        }
      }

      // Load participants
      const participantsData = type === 'match'
        ? await api.getMatchParticipants(entityId, user?.id)
        : await api.getPracticeParticipants(entityId, user?.id);
      setParticipants(participantsData);

      // Load existing summary if any
      let summary = null;
      try {
        summary = type === 'match'
          ? await api.getMatchSummary(entityId)
          : await api.getPracticeSummary(entityId);
        setExistingSummary(summary);
        setScrimmageStats(summary.scrimmageStats || null);
        setFormData({
          scoreHome: summary.scoreHome != null ? String(summary.scoreHome) : '',
          scoreAway: summary.scoreAway != null ? String(summary.scoreAway) : '',
          opponentName: summary.opponentName || '',
          manOfMatchPlayerId: summary.manOfMatchPlayerId || '',
          manOfMatchReason: summary.manOfMatchReason || '',
          nextMatchId: summary.nextMatchId || '',
          nextMatchDate: summary.nextMatchDate || '',
        });
        setUploadedPhotos(summary.photoUrls || []);
      } catch {
        setExistingSummary(null);
        if (type === 'practice') {
          const scrimmage = await loadScrimmageStats();
          if (scrimmage && !fromScrimmage) {
            applyScrimmageToForm(scrimmage);
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [type, entityId, user?.id, fromScrimmage, loadScrimmageStats, applyScrimmageToForm]);

  const autoSyncedRef = useRef(false);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (
      !fromScrimmage ||
      type !== 'practice' ||
      existingSummary ||
      !scrimmageStats ||
      autoSyncedRef.current ||
      syncing
    ) {
      return;
    }
    autoSyncedRef.current = true;
    handleSyncFromScrimmage();
  }, [fromScrimmage, type, existingSummary, scrimmageStats, syncing, handleSyncFromScrimmage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(files);
  };

  const handlePhotoUpload = async () => {
    if (photos.length === 0) return;

    try {
      setUploadingPhotos(true);
      setError('');
      
      const uploadedUrls = type === 'match'
        ? await api.uploadMatchPhotos(entityId, photos, user.id)
        : await api.uploadPracticePhotos(entityId, photos, user.id);
      
      setUploadedPhotos((prev) => [...prev, ...uploadedUrls]);
      setPhotos([]);
      setSuccess('Photos uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      const summaryId = existingSummary?.id;
      if (!summaryId) {
        setError('Summary not found. Please save the summary first.');
        return;
      }

      await api.deleteSummaryPhoto(summaryId, photoUrl);
      setUploadedPhotos((prev) => prev.filter((url) => url !== photoUrl));
      setSuccess('Photo deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete photo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const summaryData = {
        scoreHome: formData.scoreHome ? parseInt(formData.scoreHome) : null,
        scoreAway: formData.scoreAway ? parseInt(formData.scoreAway) : null,
        opponentName: formData.opponentName || null,
        manOfMatchPlayerId: formData.manOfMatchPlayerId || null,
        manOfMatchReason: formData.manOfMatchReason || null,
        nextMatchId: formData.nextMatchId || null,
        nextMatchDate: formData.nextMatchDate || null,
      };

      const saved = type === 'match'
        ? await api.createMatchSummary(entityId, summaryData, user.id)
        : await api.createPracticeSummary(entityId, summaryData, user.id);

      setExistingSummary(saved);
      if (saved.scrimmageStats) {
        setScrimmageStats(saved.scrimmageStats);
      }
      setSuccess('Summary saved! Upload team photos below, then share on WhatsApp.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to save summary');
    } finally {
      setLoading(false);
    }
  };

  const handleShareViaWhatsApp = () => {
    if (!existingSummary) {
      setError('Please save the summary first before sharing.');
      return;
    }

    const statsForShare = existingSummary.scrimmageStats || scrimmageStats;
    const shareUrl = api.shareSummaryViaWhatsApp(
      existingSummary.shareToken,
      entity?.title || 'Match Summary',
      formData.scoreHome,
      formData.scoreAway,
      type === 'practice' ? 'Red Team vs Blue Team' : formData.opponentName,
      participants.find(p => p.id === formData.manOfMatchPlayerId)?.name
        || existingSummary.manOfMatchPlayerName,
      formData.manOfMatchReason,
      uploadedPhotos,
      existingSummary.nextMatchDate,
      existingSummary.nextMatchTitle,
      statsForShare
    );
    
    window.open(shareUrl, '_blank');
  };

  const canManageBlog = () => {
    if (!user?.id || type !== 'practice') return false;
    if (isAdmin()) return true;
    if (isClubAdmin() && club?.adminUserId === user.id) return true;
    if (club?.adminUserId === user.id) return true;
    return false;
  };

  const handleCreateBlogDraft = async () => {
    if (!existingSummary || !entity?.clubId || !user?.id) {
      setError('Please save the summary first.');
      return;
    }
    try {
      setCreatingBlogDraft(true);
      setError('');
      const post = await api.createBlogFromPracticeSummary(entity.clubId, entityId, user.id);
      navigate(soccerPath(`/clubs/${entity.clubId}/admin/blog/${post.id}/edit`));
    } catch (err) {
      setError(err.message || 'Failed to create blog draft');
    } finally {
      setCreatingBlogDraft(false);
    }
  };

  if (loading && !existingSummary) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="match-summary-form">
      <div className="card">
        <h2>
          {existingSummary ? 'Update' : 'Create'} {type === 'match' ? 'Match' : 'Practice'} Summary
        </h2>
        {entity && (
          <p className="entity-title">{entity.title}</p>
        )}

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {type === 'practice' && scrimmageStats && (
          <div className="form-section match-summary-form__scrimmage">
            <div className="match-summary-form__scrimmage-header">
              <h3>Live scrimmage stats</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <Link
                  to={soccerPath(`/practices/${entityId}/scrimmage-stats`)}
                  className="btn btn-secondary btn-sm"
                  style={{ textDecoration: 'none' }}
                >
                  Edit scrimmage stats
                </Link>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleSyncFromScrimmage}
                  disabled={syncing}
                >
                  {syncing ? 'Importing…' : 'Import scores from scrimmage'}
                </button>
              </div>
            </div>
            <p className="form-help">
              Scores and Man of the Match can be pulled from the live stats you recorded during practice.
              Club admins can update scrimmage stats after the session ends — mark them final again afterward.
            </p>
            <ScrimmageStatsSummarySection scrimmageStats={scrimmageStats} compact />
          </div>
        )}

        {type === 'practice' && !scrimmageStats && (
          <p className="form-help match-summary-form__scrimmage-empty">
            No live scrimmage stats yet.{' '}
            <Link to={soccerPath(`/practices/${entityId}/scrimmage-stats`)}>
              Record live stats first
            </Link>
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Score Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>{type === 'practice' ? 'Red Team Score' : 'Home Score'}</label>
                <input
                  type="number"
                  name="scoreHome"
                  value={formData.scoreHome}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label>{type === 'practice' ? 'Blue Team Score' : 'Away Score'}</label>
                <input
                  type="number"
                  name="scoreAway"
                  value={formData.scoreAway}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>
            {type === 'match' && (
              <div className="form-group">
                <label>Opponent Name</label>
                <input
                  type="text"
                  name="opponentName"
                  value={formData.opponentName}
                  onChange={handleChange}
                  placeholder="Enter opponent name"
                />
              </div>
            )}
            {type === 'practice' && (
              <p className="form-help" style={{ marginTop: '0.5rem', fontStyle: 'italic', color: '#666' }}>
                This is an internal practice match between Red Team and Blue Team
              </p>
            )}
          </div>

          <div className="form-section">
            <h3>Man of the Match</h3>
            <div className="form-group">
              <label>Select Player</label>
              <select
                name="manOfMatchPlayerId"
                value={formData.manOfMatchPlayerId}
                onChange={handleChange}
              >
                <option value="">-- Select Player --</option>
                {participants.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.name}
                  </option>
                ))}
              </select>
              {participants.length === 0 && (
                <p className="form-help">No participants available. Players must vote "Available" to be eligible.</p>
              )}
            </div>
            <div className="form-group">
              <label>Why was this player selected as Man of the Match? (Optional)</label>
              <textarea
                name="manOfMatchReason"
                value={formData.manOfMatchReason}
                onChange={handleChange}
                placeholder="e.g., Scored 2 goals, excellent defense, great teamwork..."
                rows="3"
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Photos</h3>
            <div className="photo-upload-section">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                className="file-input"
              />
              {photos.length > 0 && (
                <div className="selected-photos">
                  <p>{photos.length} photo(s) selected</p>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handlePhotoUpload}
                    disabled={uploadingPhotos}
                  >
                    {uploadingPhotos ? 'Uploading...' : 'Upload Photos'}
                  </button>
                </div>
              )}
            </div>

            {uploadedPhotos.length > 0 && (
              <div className="uploaded-photos">
                <PhotoGallery photos={uploadedPhotos} />
                <div className="photo-actions">
                  {uploadedPhotos.map((url, index) => (
                    <button
                      key={index}
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeletePhoto(url)}
                    >
                      Delete Photo {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Next Match</h3>
            <div className="form-group">
              <label>Next Match Date (Optional)</label>
              <input
                type="datetime-local"
                name="nextMatchDate"
                value={formData.nextMatchDate}
                onChange={handleChange}
              />
              <p className="form-help">Leave empty if not scheduling next match yet</p>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Summary'}
            </button>
            {existingSummary && (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleShareViaWhatsApp}
              >
                📱 Share on WhatsApp
              </button>
            )}
            {existingSummary && canManageBlog() && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateBlogDraft}
                disabled={creatingBlogDraft}
              >
                {creatingBlogDraft ? 'Creating draft…' : 'Create blog draft'}
              </button>
            )}
            {existingSummary && canManageBlog() && (
              <p className="form-help" style={{ width: '100%', marginTop: '0.5rem' }}>
                Opens a draft blog post with score, stats, photos, and a link to the public summary. Review and publish from the blog editor.
              </p>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MatchSummaryForm;

